import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, MessageCircle, Mail } from 'lucide-react';
import { toPng, toBlob } from 'html-to-image';

export default function OrderConfirmationPage({ setPage }) {
  const imageRef = useRef(null);
  const [companyName, setCompanyName] = useState('Your Company');
  const [showPreview, setShowPreview] = useState(false);
  const API_BASE_URL = window._config?.apiBaseUrl || '';
  const DATABASE_KEY = window._config?.databaseKey || '';
  const poid = localStorage.getItem('poid') || '0';
  const vcode = localStorage.getItem('vcode') || '';
  const vname = localStorage.getItem('vname') || 'Vendor';
  const address = localStorage.getItem('deliveryAddress') || localStorage.getItem('cusAddress') || 'N/A';
  const deliveryDate = localStorage.getItem('deliveryDate') || new Date().toLocaleDateString();
  const paymode = localStorage.getItem('paymode') || 'UPI';
  const amount = localStorage.getItem('amountPaid') || '0.00';
  const ref = localStorage.getItem('referenceNo') || 'N/A';
  const email = localStorage.getItem('mail') || '';

  const cartData = JSON.parse(localStorage.getItem('orderCart') || '[]');
  const totalQty = cartData.reduce(
  (sum, item) => sum + Number(item.quantity || 0),
  0
);
  const total = parseFloat(localStorage.getItem('orderTotal') || 0);
  const netTotal = total.toFixed(2);

  useEffect(() => {
    fetch('/config.json')
      .then((res) => res.json())
      .then((data) => {
        if (data.CompanyName) setCompanyName(data.CompanyName);
      })
      .catch(console.error);
  }, []);

const generateAndOpenImage = async () => {
  setShowPreview(true); // Render hidden image block

  setTimeout(async () => {
    try {
      const node = imageRef.current;
      if (!node) throw new Error('Image block not found.');

      const blob = await toBlob(node, {
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      if (blob && navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      const link = document.createElement('a');
     const itemDetails = cartData
  .map(
    (item, i) =>
      `${i + 1}. Part No: ${item.partno || item.item_no || '-'}
Qty: ${item.quantity}
`
  )
  .join('\n\n');

// Full message
const message = `*ORDER CONFIRMATION*

Company: ${companyName}
Order ID: ${poid}
Delivery Date: ${deliveryDate}

Customer: ${vname}

Items:
${itemDetails}

Thank you for your order!`;

// Encode message properly
const encodedMessage = encodeURIComponent(message);

// Remove + if exists in phone number
const cleanNumber = vcode.replace('+', '');

link.href = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Image generation or clipboard failed:', err);
    } finally {
      setShowPreview(false);
    }
  }, 500); // Let DOM paint before capture
};


  const handleSendEmail = async () => {
    try {
      const res = await fetch(
  `${API_BASE_URL}/SendOrderEmail?Mail=${encodeURIComponent(email)}&databaseKey=${encodeURIComponent(DATABASE_KEY)}`
);
      const json = await res.json();
      const result = json?.SendOrderEmailJs?.Table?.[0];
      if (result?.Status === 'Success') {
        alert('📧 Order confirmation email sent!');
      } else {
        alert('❌ Email failed.');
      }
    } catch (err) {
      alert('❌ Email error.');
    }
  };

  const thStyle = {
    border: '1px solid #ccc',
    padding: '8px',
    textAlign: 'left',
    backgroundColor: '#f2f2f2',
  };

  const tdStyle = {
    border: '1px solid #ccc',
    padding: '8px',
    textAlign: 'left',
  };

return (
  <div className="min-h-screen bg-gradient-to-br from-pink-100 via-rose-50 to-teal-100 flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-3xl">

      {/* Success Card */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/40">

        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-4 rounded-full">
              <CheckCircle className="text-green-600 h-14 w-14" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-teal-700">
            Order Placed Successfully!
          </h2>

          <p className="text-gray-600 mt-2">
            Thank you for shopping with <strong>{companyName}</strong>
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-8 border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Order Summary
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <p><span className="font-medium">Order ID:</span> {poid}</p>
              <p><span className="font-medium">Delivery Date:</span> {deliveryDate}</p>
              <p><span className="font-medium">Customer:</span> {vname}</p>
            </div>

            <div className="sm:text-right">
              
              <p><span className="font-medium">Reference:</span> {ref}</p>
                <p className="text-lg font-bold text-teal-600 mt-2">
    Total Quantity: {totalQty}
  </p>
            </div>
          </div>

          <div className="mt-4">
            <p className="font-medium">Delivery Address:</p>
            <p className="text-gray-600">{address}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">

          <button
            onClick={() => setPage('products')}
            className="flex-1 bg-gradient-to-r from-teal-500 to-rose-400 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:scale-105 transition transform"
          >
            Continue Shopping
          </button>

          <button
            onClick={generateAndOpenImage}
            className="flex-1 bg-green-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-green-600 transition flex items-center justify-center gap-2"
          >
            <MessageCircle size={18} />
            Share via WhatsApp
          </button>

          <button
            onClick={handleSendEmail}
            className="flex-1 bg-blue-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-blue-600 transition flex items-center justify-center gap-2"
          >
            <Mail size={18} />
            Send Email
          </button>

        </div>
      </div>

      {/* Footer Note */}
      <div className="text-center text-sm text-gray-500 mt-6">
        A confirmation email has been sent to <strong>{email}</strong>
      </div>

    </div>

    {/* Hidden Image Preview (unchanged functionality) */}
    {showPreview && (
      <div
        ref={imageRef}
        style={{
          maxWidth: '650px',
          margin: '30px auto',
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          padding: '35px',
          fontFamily: 'Inter, Arial, sans-serif',
          fontSize: '14px',
          color: '#333',
          lineHeight: 1.6,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '25px' }}>
          <h2 style={{ margin: 0, color: '#0f766e', fontSize: '22px' }}>
            ORDER CONFIRMATION
          </h2>
          <p style={{ margin: '8px 0', fontSize: '14px' }}>
            <strong>{companyName}</strong>
          </p>
          <p style={{ fontSize: '13px', color: '#555' }}>
            Dear {vname}, your order has been successfully placed.
          </p>
        </div>

        {/* Order Details */}
        <div style={{ marginBottom: '15px' }}>
          <p><strong>Order ID:</strong> {poid}</p>
          <p><strong>Delivery Date:</strong> {deliveryDate}</p>
          <p><strong>Payment Mode:</strong> {paymode}</p>
          <p><strong>Reference:</strong> {ref}</p>
        </div>

        {/* Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '20px',
            fontSize: '13px'
          }}
        >
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Part No</th>
              <th style={thStyle}>Description</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Qty</th>

            </tr>
          </thead>
         <tbody>
  {cartData.map((item, i) => (
    <tr key={i}>
      <td style={tdStyle}>{i + 1}</td>
      <td style={tdStyle}>{item.partno || item.item_no || '-'}</td>
      <td style={tdStyle}>{item.name}</td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        {item.quantity}
      </td>
    </tr>
  ))}
</tbody>
        </table>

        {/* Delivery Address */}
        <div>
          <strong>Delivery Address:</strong>
          <div>{address}</div>
        </div>
      </div>
    )}
  </div>
);

}
